import { useEffect, useRef, useState } from 'react';

type UseBrowserVideoPreviewOptions = {
  videoDeviceId?: string;
  isVideoEnabled?: boolean;
};

type UseBrowserVideoPreviewResult = {
  stream: MediaStream | null;
  error: Error | null;
  isLoading: boolean;
};

/**
 * Hook to get video preview directly from browser MediaDevices API
 * without requiring an active Chime meeting session.
 *
 * @param options - Configuration options
 * @param options.videoDeviceId - ID of the video device to use
 * @param options.isVideoEnabled - Whether video should be enabled
 * @returns Object containing the video stream, error state, and loading state
 *
 * @example
 * const { stream, error, isLoading } = useBrowserVideoPreview({
 *   videoDeviceId: selectedCamera,
 *   isVideoEnabled: true
 * });
 */
export default function useBrowserVideoPreview({
  videoDeviceId,
  isVideoEnabled = true,
}: UseBrowserVideoPreviewOptions = {}): UseBrowserVideoPreviewResult {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const currentStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    // Don't request stream if video is disabled
    if (!isVideoEnabled) {
      // Clean up existing stream
      if (currentStreamRef.current) {
        currentStreamRef.current.getTracks().forEach((track) => track.stop());
        currentStreamRef.current = null;
      }
      setStream(null);
      setError(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    const constraints: MediaStreamConstraints = {
      video: videoDeviceId
        ? {
            deviceId: { exact: videoDeviceId },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          }
        : {
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
      audio: false, // We only want video preview
    };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((mediaStream) => {
        if (!isMounted) {
          // Component unmounted before stream ready, clean up
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }

        // Stop previous stream if exists
        if (currentStreamRef.current) {
          currentStreamRef.current.getTracks().forEach((track) => track.stop());
        }

        currentStreamRef.current = mediaStream;
        setStream(mediaStream);
        setIsLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;

        console.error('Error accessing video device:', err);
        setError(
          err instanceof Error
            ? err
            : new Error('Impossible d\'accéder à la caméra')
        );
        setStream(null);
        setIsLoading(false);
      });

    // Cleanup function
    return () => {
      isMounted = false;
      if (currentStreamRef.current) {
        currentStreamRef.current.getTracks().forEach((track) => track.stop());
        currentStreamRef.current = null;
      }
    };
  }, [videoDeviceId, isVideoEnabled]);

  return { stream, error, isLoading };
}
